import { useEffect, useRef, useState } from 'react';
import { useMyServices } from '../../graphql/queries';
import { createServiceAssignment, ApiError } from '../../lib/apiClient';
import type { CreateServiceAssignmentInput, AssignmentStatusInput } from '../../lib/apiClient';
import type { ProjectMilestone, ProjectOutcome, ServiceAssignment } from '../../graphql/types';
import '@tylertech/forge/dialog';
import '@tylertech/forge/button';
import '@tylertech/forge/banner';
import './AssignServiceDialog.css';

export interface AssignServiceDialogProps {
  open: boolean;
  milestones: ProjectMilestone[];
  outcomes: ProjectOutcome[];
  onSuccess: (assignment: ServiceAssignment) => void;
  onCancel: () => void;
}

const EMPTY_FORM = {
  serviceId: '',
  milestoneId: '',
  outcomeId: '',
  assignmentStatus: 'planned' as AssignmentStatusInput,
  statusNote: '',
};

export function AssignServiceDialog({
  open,
  milestones,
  outcomes,
  onSuccess,
  onCancel,
}: AssignServiceDialogProps): JSX.Element {
  const dialogRef = useRef<HTMLElement & { open: boolean }>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: myServicesData } = useMyServices(1000);
  const ownedServices = myServicesData?.myServices?.edges?.map((e) => e.node) ?? [];

  // Filterable picker (issue #655) — narrows the Service select's options by
  // name as the user types, scoped to the same accessible (owned) services
  // list as before; the underlying control stays a native <select> so
  // existing keyboard/screen-reader behavior and form semantics are unchanged.
  // The currently selected service is always kept in the option list even if
  // it no longer matches the filter text, so typing after selecting a value
  // never silently clears the selection out from under the user.
  const [serviceFilter, setServiceFilter] = useState('');
  const filterLower = serviceFilter.trim().toLowerCase();
  const filteredServices = ownedServices.filter(
    (svc) => svc.entityId === form.serviceId || svc.name.toLowerCase().includes(filterLower)
  );

  const DialogElement = 'forge-dialog' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;

  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.open = open;
    }
    if (open) {
      setForm(EMPTY_FORM);
      setError(null);
      setServiceFilter('');
    }
  }, [open]);

  const handleClose = (): void => {
    if (!isSubmitting) {
      onCancel();
    }
  };

  const canSubmit =
    form.serviceId !== '' && form.milestoneId !== '' && form.outcomeId !== '' && !isSubmitting;

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const input: CreateServiceAssignmentInput = {
        milestoneId: form.milestoneId,
        outcomeId: form.outcomeId,
        assignmentStatus: form.assignmentStatus,
        statusNote: form.statusNote.trim() || undefined,
      };
      const assignment = await createServiceAssignment(form.serviceId, input);
      onSuccess(assignment);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to assign service');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogElement
      ref={dialogRef}
      onForge-dialog-close={handleClose}
      className="assign-service-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="assign-dialog-title"
    >
      <div className="dialog-content">
        <h2 id="assign-dialog-title" className="forge-typography--headline5 assign-dialog-title">
          Assign Service
        </h2>

        {error && (
          <BannerElement
            theme="error"
            dismissed={false}
            role="alert"
            className="assign-dialog-error"
          >
            <span>{error}</span>
          </BannerElement>
        )}

        <div className="assign-dialog-body">
          <div className="assign-dialog-field">
            <label className="forge-typography--caption assign-dialog-label" htmlFor="asd-service">
              Service *
            </label>
            <input
              type="text"
              className="search-input assign-dialog-filter"
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              placeholder="Filter services…"
              aria-label="Filter services"
            />
            <select
              id="asd-service"
              className="filter-select assign-dialog-select"
              value={form.serviceId}
              onChange={(e) => setForm((p) => ({ ...p, serviceId: e.target.value }))}
              aria-required="true"
            >
              <option value="">— Select a service —</option>
              {filteredServices.map((svc) => (
                <option key={svc.entityId} value={svc.entityId}>
                  {svc.name}
                </option>
              ))}
            </select>
          </div>

          <div className="assign-dialog-field">
            <label
              className="forge-typography--caption assign-dialog-label"
              htmlFor="asd-milestone"
            >
              Milestone *
            </label>
            <select
              id="asd-milestone"
              className="filter-select assign-dialog-select"
              value={form.milestoneId}
              onChange={(e) => setForm((p) => ({ ...p, milestoneId: e.target.value }))}
              aria-required="true"
            >
              <option value="">— Select a milestone —</option>
              {milestones.map((ms) => (
                <option key={ms.entityId} value={ms.entityId}>
                  {ms.name}
                </option>
              ))}
            </select>
          </div>

          <div className="assign-dialog-field">
            <label className="forge-typography--caption assign-dialog-label" htmlFor="asd-outcome">
              Outcome *
            </label>
            <select
              id="asd-outcome"
              className="filter-select assign-dialog-select"
              value={form.outcomeId}
              onChange={(e) => setForm((p) => ({ ...p, outcomeId: e.target.value }))}
              aria-required="true"
            >
              <option value="">— Select an outcome —</option>
              {outcomes.map((oc) => (
                <option key={oc.entityId} value={oc.entityId}>
                  {oc.name}
                </option>
              ))}
            </select>
          </div>

          <div className="assign-dialog-field">
            <label className="forge-typography--caption assign-dialog-label" htmlFor="asd-status">
              Status
            </label>
            <select
              id="asd-status"
              className="filter-select assign-dialog-select"
              value={form.assignmentStatus}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  assignmentStatus: e.target.value as AssignmentStatusInput,
                }))
              }
            >
              <option value="planned">Planned</option>
              <option value="in-progress">In Progress</option>
              <option value="done">Done</option>
            </select>
          </div>

          <div className="assign-dialog-field">
            <label className="forge-typography--caption assign-dialog-label" htmlFor="asd-note">
              Status Note
            </label>
            <input
              id="asd-note"
              className="search-input"
              value={form.statusNote}
              onChange={(e) => setForm((p) => ({ ...p, statusNote: e.target.value }))}
              placeholder="Optional note"
              aria-label="Status note"
            />
          </div>
        </div>

        <div className="dialog-actions">
          <ForgeButton type="button" variant="text" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </ForgeButton>
          <ForgeButton
            type="button"
            variant="raised"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
          >
            {isSubmitting ? 'Assigning...' : 'Assign'}
          </ForgeButton>
        </div>
      </div>
    </DialogElement>
  );
}
